import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { useResources } from '@ctw/shared/context/_internal/resource/resource-fetch';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import {
  type FetchableModelType,
  type FetchableResourceType,
  isFetchableResourceType,
} from '@ctw/shared/utils/fhir/resource-mappings';
import {
  type ReferenceWithOptionalSummary,
  type ZusDomainResourceInterface,
  normalizeAndOrderRiskGapEvidence,
} from '@zeus-health/types';
import { useMemo } from 'react';
import { extractQuotesFromSummary } from './extract-quote-from-summary';

export type EvidenceModel = FetchableModelType<FetchableResourceType>;

export type EvidenceItem = { resource: EvidenceModel; quotes: Array<string> };

export const EVIDENCE_SECTION_ORDER = [
  'Observations',
  'Diagnostic Reports',
  'Medications',
  'Documents',
  'Diagnoses',
  'Claims',
  'Payer Gaps',
] as const;

export type EvidenceSectionKey = (typeof EVIDENCE_SECTION_ORDER)[number];

export const RESOURCE_TYPE_TO_SECTION: Record<string, EvidenceSectionKey> = {
  Observation: 'Observations',
  DiagnosticReport: 'Diagnostic Reports',
  MedicationStatement: 'Medications',
  DocumentReference: 'Documents',
  Condition: 'Diagnoses',
  Claim: 'Claims',
  MeasureReport: 'Payer Gaps',
};

function useCanonicalEvidenceItems(
  riskGap: RiskGapModel,
  resources: Array<EvidenceModel>,
): Array<EvidenceModel> {
  return useMemo((): Array<EvidenceModel> => {
    const evidenceReferences: Array<ReferenceWithOptionalSummary> = riskGap.evidenceReferences.map(
      (ref) => ({
        reference: `${ref.resourceType}/${ref.resourceId}`,
        summary: ref.summary,
      }),
    );

    const { orderedItems } = normalizeAndOrderRiskGapEvidence({
      evidenceReferences,
      resolvedResources: resources.map((r) => r.resource as unknown as ZusDomainResourceInterface),
    });

    const resourcesByKey = new Map<string, EvidenceModel>();
    for (const r of resources) {
      resourcesByKey.set(`${r.resourceType}/${r.id}`, r);
    }

    return orderedItems
      .map((item) => resourcesByKey.get(item.key))
      .filter((r): r is EvidenceModel => r !== undefined);
  }, [riskGap.evidenceReferences, resources]);
}

export function useEvidenceResources(riskGap: RiskGapModel) {
  const { patient } = usePatientContext();

  const identifiers = useMemo(
    () =>
      riskGap.evidenceReferences
        .filter((ref) => isFetchableResourceType(ref.resourceType))
        .map((ref) => ({
          resourceId: ref.resourceId,
          resourceType: ref.resourceType as FetchableResourceType,
        })),
    [riskGap.evidenceReferences],
  );

  const { data: resources = [], isLoading } = useResources<EvidenceModel>({
    identifiers,
    patient,
  });

  const orderedResources = useCanonicalEvidenceItems(riskGap, resources);

  const evidenceItems: Array<EvidenceItem> = useMemo(() => {
    const quotesByKey = new Map<string, Array<string>>();
    for (const ref of riskGap.evidenceReferences) {
      if (ref.resourceType === 'DocumentReference') {
        const quotes = extractQuotesFromSummary(ref.summary);
        if (quotes.length > 0) {
          quotesByKey.set(`${ref.resourceType}/${ref.resourceId}`, quotes);
        }
      }
    }
    return orderedResources.map((resource) => ({
      resource,
      quotes: quotesByKey.get(`${resource.resourceType}/${resource.id}`) ?? [],
    }));
  }, [orderedResources, riskGap.evidenceReferences]);

  return {
    evidenceItems,
    isLoading,
    hasEvidence: identifiers.length > 0,
    gapSummaryContent: riskGap.gapSummaryContent,
  };
}

export type UseEvidenceResourcesResult = ReturnType<typeof useEvidenceResources>;
